import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getPastGenerations } from '../../services/userService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProgressiveImage from '../ui/ProgressiveImage';
import CustomSelect from '../ui/CustomSelect';

interface Generation {
  generationId: string;
  imageUrlList: string[];
  inputImageUrls: string[];
  prompt: string;
  styleName: string;
  timestamp: string;
  type: 'photoshoot' | 'try-on' | 'realistic-try-on' | 'fashion_extractor' | 'ghost_mannequin' | 'ghost-mannequin' | 'ai_ghost_mannequin' | 'ai-ghost-mannequin' | 'ai_wrinkle_removal' | 'wrinkle-removal' | 'video-generation' | 'upscaler' | 'ai-poses' | 'background-removal' | 'ai-designer' | 'image-editing' | 'background-change' | 'ai-workflow' | 'ai_workflow' | 'vton' | 'ecommerce-catalogue-generator' | 'active-wear-catalogue-generator' | 'yoga-wear-catalogue-generator';
  userId: string;
  creditCost: number;
  numberOfImages: number;
  numberOfVideos: number;
  videoUrlList?: string[];
  // Potential extra fields from backend for workflows
  workflowType?: string;
  templateId?: string;
  templateUrl?: string;
  backgroundId?: string;
  backgroundUrl?: string;
  selectedCatalogueModelIds?: string[];
  selectedCatalogueModelUrls?: string[];
  selectedCataloguePoses?: any[];
  mode?: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}



const Gallery: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters
  const [filter, setFilter] = useState<'all' | 'photoshoot' | 'try-on' | 'realistic-try-on' | 'fashion_extractor' | 'ghost-mannequin' | 'wrinkle-removal' | 'ai_wrinkle_removal' | 'video-generation' | 'upscaler' | 'ai-poses' | 'background-removal' | 'ai-designer' | 'image-editing' | 'background-change' | 'ai_workflow' | 'vton'>(
    (searchParams.get('type') as any) || 'all'
  );
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  // Pagination state from URL
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('limit') || '12'));
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);

  // Update URL when state changes
  const updateURLParams = (newParams: { page?: number; type?: string; search?: string; limit?: number }) => {
    const updatedParams = new URLSearchParams(searchParams);

    // Update or remove parameters
    if (newParams.page !== undefined) {
      if (newParams.page === 1) {
        updatedParams.delete('page');
      } else {
        updatedParams.set('page', newParams.page.toString());
      }
    }

    if (newParams.type !== undefined) {
      if (newParams.type === 'all') {
        updatedParams.delete('type');
      } else {
        updatedParams.set('type', newParams.type);
      }
    }

    if (newParams.search !== undefined) {
      if (!newParams.search) {
        updatedParams.delete('search');
      } else {
        updatedParams.set('search', newParams.search);
      }
    }

    if (newParams.limit !== undefined) {
      if (newParams.limit === 12) {
        updatedParams.delete('limit');
      } else {
        updatedParams.set('limit', newParams.limit.toString());
      }
    }

    setSearchParams(updatedParams);
  };

  // Debounce search term with smarter logic
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setIsDebouncing(true);
      // KEY FIX: Clear generations immediately when search changes
      setGenerations([]);
    }

    // Use longer delay for better user experience and fewer API calls
    const timer = setTimeout(() => {
      // Only search if term is empty (show all) or has 2+ characters
      const shouldSearch = searchTerm.length === 0 || searchTerm.length >= 2;

      if (shouldSearch && searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setCurrentPage(1); // Reset to first page when search changes
        // Remove updateURLParams call here to prevent infinite loops
      }
      setIsDebouncing(false);
    }, searchTerm.length > 5 ? 1200 : 800); // Longer delay for longer searches

    return () => {
      clearTimeout(timer);
      setIsDebouncing(false);
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Map frontend filter values to backend expected values
  const mapFilterToBackend = useCallback((frontendFilter: string) => {
    switch (frontendFilter) {
      case 'all':
        return 'all';
      case 'photoshoot':
        return 'photoshoot';
      case 'try-on':
        return 'try-on';
      case 'realistic-try-on':
        return 'realistic-try-on';
      case 'fashion-extractor':
        return 'fashion_extractor';
      case 'ghost-mannequin':
        return 'ai-ghost-mannequin';
      case 'wrinkle-removal':
      case 'ai-wrinkle-removal':
      case 'ai_wrinkle_removal':
        return 'ai_wrinkle_removal';
      case 'ai-poses':
        return 'ai-poses';
      case 'video-generation':
        return 'video-generation';
      case 'upscaler':
        return 'upscaler';
      case 'background-removal':
        return 'background-removal';
      case 'ai-designer':
        return 'ai-designer';
      case 'image-editing':
        return 'image-editing';
      case 'background-change':
        return 'background-change';
      case 'ai-workflow':
      case 'ai_workflow':
        return 'ai-workflow';
      case 'vton':
        return 'vton';
      default:
        return 'all';
    }
  }, []);

  useEffect(() => {
    const trackPageView = (page: number, type: string, search: string) => {
      console.log('Gallery Page View:', {
        page,
        type,
        search: search || 'none',
        timestamp: new Date().toISOString()
      });
    };

    const fetchPastGenerations = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const backendFilter = mapFilterToBackend(filter);

        const response = await getPastGenerations(
          currentUser.uid,
          currentPage,
          itemsPerPage,
          backendFilter,
          debouncedSearchTerm || undefined
        );

        if (response.success) {
          setGenerations(response.data);
          setPaginationInfo(response.pagination);
          // Track page view for analytics
          trackPageView(currentPage, filter, debouncedSearchTerm);
        } else {
          setError(response.message || 'Failed to load your past generations.');
        }
      } catch (err: any) {
        console.error('Failed to fetch past generations:', err);
        setError('Failed to load your past generations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPastGenerations();
  }, [currentUser, currentPage, itemsPerPage, filter, debouncedSearchTerm, mapFilterToBackend]);

  // Remove the local filtering since it's now handled by the backend
  const filteredGenerations = generations;

  // Map backend type to display name; for AI Workflow use styleName to show "AI Workflow - <workflow name>"
  const getDisplayType = useCallback((type: string, styleName?: string) => {
    if (type === 'ai-workflow' || type === 'ai_workflow') {
      const workflowLabel = styleName
        ? (styleName === 'catalogue-generator'
          ? 'Ecommerce Photoshoot'
          : styleName === 'stitched-cloths'
            ? 'Stitched Cloths'
            : styleName === 'front-back-shoot'
              ? 'Front & Back Shoot'
              : styleName)
        : null;
      return workflowLabel ? `AI Workflow - ${workflowLabel}` : 'AI Workflow';
    }
    switch (type) {
      case 'photoshoot':
        return 'AI Photoshoot';
      case 'try-on':
        return 'Clothes AI Photoshoot';
      case 'realistic-try-on':
        return 'Advance Clothes Try-On';
      case 'ai-poses':
        return 'Pose Change';
      case 'fashion-extractor':
      case 'fashion_extractor':
        return 'Outfit Extractor (Flatlay)';
      case 'ghost-mannequin':
      case 'ghost_mannequin':
      case 'ai-ghost-mannequin':
      case 'ai_ghost_mannequin':
        return 'Ghost Mannequin';
      case 'wrinkle-removal':
      case 'ai-wrinkle-removal':
      case 'ai_wrinkle_removal':
        return 'Wrinkle Removal';
      case 'video-generation':
        return 'Video Generation';
      case 'upscaler':
        return 'Image Upscaler';
      case 'background-removal':
        return 'Background Removal';
      case 'background-change':
        return 'Background Change';
      case 'ai-designer':
        return 'AI Designer';
      case 'image-editing':
        return 'Image Editing';
      case 'vton':
        return 'VTON';
      case 'ecommerce-catalogue-generator':
        return 'Ecommerce Photoshoot';
      case 'active-wear-catalogue-generator':
        return 'Gym Wear Photoshoot';
      case 'yoga-wear-catalogue-generator':
        return 'Yoga Wear Photoshoot';
      default:
        return type;
    }
  }, []);

  // Compute UI state for optimized rendering
  const uiState = useMemo(() => {
    const isLoading = loading || (isDebouncing && searchTerm.length >= 2);
    const showEmpty = !isLoading && !isDebouncing && !error && filteredGenerations.length === 0;
    const showGrid = !isLoading && !error && filteredGenerations.length > 0;
    const showPagination = showGrid && paginationInfo;

    return { isLoading, showEmpty, showGrid, showPagination };
  }, [loading, isDebouncing, searchTerm.length, error, filteredGenerations.length, paginationInfo]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleRecreate = (generation: Generation) => {
    let path = '/photoshoot';
    if (generation.type === 'try-on') {
      path = '/try-on';
    } else if (generation.type === 'realistic-try-on') {
      path = '/realistic-photoshoot';
    } else if (generation.type === 'ai-poses') {
      path = '/ai-poses';
    } else if (generation.type === 'fashion_extractor') {
      path = '/fashion-extractor';
    } else if (
      generation.type === 'ghost_mannequin' ||
      generation.type === 'ghost-mannequin' ||
      generation.type === 'ai_ghost_mannequin' ||
      generation.type === 'ai-ghost-mannequin'
    ) {
      path = '/ghost-mannequin';
    } else if (generation.type === 'ai_wrinkle_removal' || generation.type === 'wrinkle-removal') {
      path = '/wrinkle-removal';
    } else if (generation.type === 'ai-designer') {
      path = '/ai-designer';
    } else if (generation.type === 'image-editing') {
      path = '/image-editing';
    } else if (generation.type === 'background-change') {
      path = '/ai-background-change';
    } else if (generation.type === 'video-generation') {
      path = '/canvas-editor';
    } else if (generation.type === 'upscaler') {
      path = '/canvas-editor';
    } else if (generation.type === 'background-removal') {
      path = '/canvas-editor';
    } else if (generation.type === 'ai_workflow') {
      path = '/ai-workflow';
    } else if ((generation.type as string) === 'ai-workflow') {
      path = '/ai-workflow';
    } else if (generation.type === 'ecommerce-catalogue-generator') {
      path = '/ecommerce-photoshoot';
    } else if (generation.type === 'active-wear-catalogue-generator') {
      path = '/gym-wear-photoshoot';
    } else if (generation.type === 'yoga-wear-catalogue-generator') {
      path = '/yoga-wear-photoshoot';
    } else if (generation.type === 'vton') {
      path = '/internal/vton';
    }

    const isEcommerceCatalogue = generation.type === 'ecommerce-catalogue-generator'
      || generation.type === 'active-wear-catalogue-generator'
      || generation.type === 'yoga-wear-catalogue-generator';
    const isAIWorkflow = generation.type === 'ai_workflow' || (generation.type as string) === 'ai-workflow' || isEcommerceCatalogue;
    let inputImages = generation.inputImageUrls;
    let modelImageUrl: string | undefined;
    let backgroundImageUrl: string | undefined;
    if (isAIWorkflow && !isEcommerceCatalogue && generation.inputImageUrls?.length >= 3) {
      const urls = generation.inputImageUrls;
      inputImages = urls.slice(0, -2);
      modelImageUrl = urls[urls.length - 2];
      backgroundImageUrl = urls[urls.length - 1];
    }

    // Prepare state based on generation type
    const navigationState = {
      inputImages: isAIWorkflow ? inputImages : generation.inputImageUrls,
      prompt: generation.prompt,
      style: generation.styleName,
      variations: generation.numberOfImages,
      recreatingFrom: generation.generationId,
      ...(generation.type === 'video-generation'
        ? { videoUrlList: generation.videoUrlList, tool: 'video-generation' }
        : generation.type === 'upscaler'
          ? { generatedImages: generation.imageUrlList, tool: 'upscaler' }
          : generation.type === 'background-removal'
            ? { generatedImages: generation.imageUrlList, tool: 'background-removal' }
            : isAIWorkflow
              ? {
                generatedImages: generation.imageUrlList,
                workflowType: isEcommerceCatalogue ? 'catalogue-generator' : (generation.workflowType || generation.styleName),
                modelImageUrl,
                backgroundImageUrl,
                templateId: generation.templateId,
                templateUrl: generation.templateUrl,
                backgroundId: generation.backgroundId,
                backgroundUrl: generation.backgroundUrl,
                selectedCatalogueModelIds: generation.selectedCatalogueModelIds,
                selectedCatalogueModelUrls: generation.selectedCatalogueModelUrls,
                selectedCataloguePoses: generation.selectedCataloguePoses,
                mode: generation.mode,
              }
              : { generatedImages: generation.imageUrlList }
      )
    };

    navigate(path, { state: navigationState });
  };

  const handleDownloadAll = async (generation: Generation) => {
    // Add to downloading set
    setDownloadingIds(prev => new Set(Array.from(prev).concat(generation.generationId)));

    const downloadFile = async (url: string, filename: string) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Cleanup blob URL to free memory
        window.URL.revokeObjectURL(blobUrl);
        // Small delay between downloads to avoid overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${filename}:`, error);
        // Fallback to opening in new tab if download fails
        window.open(url, '_blank');
      }
    };

    try {
      // Download all images
      if (generation.imageUrlList && generation.imageUrlList.length > 0) {
        for (let i = 0; i < generation.imageUrlList.length; i++) {
          const url = generation.imageUrlList[i];
          const filename = `${generation.type}-${generation.generationId}-image-${i + 1}.jpg`;
          await downloadFile(url, filename);
        }
      }

      // Download all videos
      if (generation.videoUrlList && generation.videoUrlList.length > 0) {
        for (let i = 0; i < generation.videoUrlList.length; i++) {
          const url = generation.videoUrlList[i];
          const filename = `${generation.type}-${generation.generationId}-video-${i + 1}.mp4`;
          await downloadFile(url, filename);
        }
      }
    } finally {
      // Remove from downloading set
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(generation.generationId);
        return newSet;
      });

      // Show download complete alert
      const totalFiles = (generation.imageUrlList?.length || 0) + (generation.videoUrlList?.length || 0);
      alert(`Download complete! ${totalFiles} file${totalFiles !== 1 ? 's' : ''} downloaded successfully.`);
    }
  };

  const handleShare = async (generation: Generation) => {
    const shareUrls = generation.imageUrlList || generation.videoUrlList || [];
    if (shareUrls.length === 0) {
      alert('No content available to share');
      return;
    }

    const getReadableType = (type: string) => {
      switch (type) {
        case 'video-generation': return 'video';
        case 'try-on': return 'virtual try-on';
        case 'realistic-try-on': return 'Advance Clothes Try-On';
        case 'ai-poses': return 'pose change';
        case 'fashion-extractor': return 'fashion extract';
        case 'ghost-mannequin':
        case 'ghost_mannequin':
        case 'ai-ghost-mannequin':
        case 'ai_ghost_mannequin':
          return 'ghost mannequin';
        case 'background-change': return 'background change';
        case 'image-editing': return 'image editing';
        case 'background-removal': return 'background removal';
        case 'upscaler': return 'image upscaler';
        case 'ai-designer': return 'AI designer';
        case 'photoshoot': return 'AI photoshoot';
        case 'vton': return 'virtual try-on (VTON)';
        default: return type;
      }
    };

    const formatUrls = (urls: string[], label: string) => {
      if (!urls || urls.length === 0) return '';

      // Split any URLs that might be concatenated with various whitespace characters
      const cleanUrls = urls
        .flatMap(url => url.split(/\s+/)) // Split on any whitespace (spaces, newlines, tabs)
        .map(url => url.trim())
        .filter(url => url.length > 0 && (url.startsWith('http') || url.startsWith('https')))
        .filter((url, index, array) => array.indexOf(url) === index); // Remove duplicates

      return cleanUrls.map((url, index) => `${label} ${index + 1}: ${url}`).join('\n');
    };

    let shareTextContent = `Check out this AI-generated ${getReadableType(generation.type)}!\n\nCreated with www.snapmydesign.com\n\n`;

    // Add all video URLs if they exist
    if (generation.videoUrlList && generation.videoUrlList.length > 0) {
      shareTextContent += formatUrls(generation.videoUrlList, 'Video');
      shareTextContent += '\n';
    }

    // Add all image URLs if they exist
    if (generation.imageUrlList && generation.imageUrlList.length > 0) {
      shareTextContent += formatUrls(generation.imageUrlList, 'Image');
      shareTextContent += '\n';
    }

    const shareText = shareTextContent;

    // Try Web Share API with actual files (works on mobile and some desktop browsers)
    if (navigator.share) {
      try {
        // Try to share actual image files
        const files: File[] = [];
        for (let i = 0; i < Math.min(shareUrls.length, 5); i++) { // Limit to 5 files to avoid overwhelming
          try {
            const response = await fetch(shareUrls[i]);
            const blob = await response.blob();
            const fileExtension = generation.videoUrlList?.includes(shareUrls[i]) ? 'mp4' : 'jpg';
            const fileName = `ai-${generation.type}-${i + 1}.${fileExtension}`;
            const file = new File([blob], fileName, { type: blob.type });
            files.push(file);
          } catch (fetchError) {
            console.log(`Failed to fetch file ${i + 1}, skipping:`, fetchError);
          }
        }

        if (files.length > 0) {
          await navigator.share({
            title: `AI Generated ${generation.type}`,
            text: shareText,
            files: files,
          });
          return;
        } else {
          // Fallback to sharing URLs if file sharing fails
          await navigator.share({
            title: `AI Generated ${generation.type}`,
            text: shareText,
            url: shareUrls[0],
          });
          return;
        }
      } catch (error) {
        console.log('Web Share API failed, falling back to platform-specific sharing');
      }
    }

    // Fallback: Show share options modal
    const shareOptions = [
      {
        name: 'WhatsApp',
        icon: 'fab fa-whatsapp',
        color: 'bg-green-500',
        url: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
        action: 'open'
      },
      {
        name: 'Twitter',
        icon: 'fab fa-twitter',
        color: 'bg-blue-500',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)})}`,
        action: 'open'
      },
      {
        name: 'Facebook',
        icon: 'fab fa-facebook',
        color: 'bg-blue-600',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareText)}`,
        action: 'open'
      },
      {
        name: 'LinkedIn',
        icon: 'fab fa-linkedin',
        color: 'bg-blue-700',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareText)}`,
        action: 'open'
      },
      {
        name: 'Copy Link',
        icon: 'fas fa-link',
        color: 'bg-accent-sand0',
        url: '',
        action: 'copy'
      }
    ];

    // Create and show share modal
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-sm w-full">
        <h3 class="text-lg font-semibold mb-4 text-center">Share</h3>
        <div class="grid grid-cols-2 gap-3">
                     ${shareOptions.map(option => `
             <button class="share-option flex flex-col items-center p-3 rounded-lg hover:bg-accent-sand transition-colors" data-action="${option.action}" data-url="${option.url}">
               <div class="w-10 h-10 ${option.color} rounded-full flex items-center justify-center mb-2">
                 <i class="${option.icon} text-white text-lg"></i>
               </div>
               <span class="text-sm font-medium text-text-heading">${option.name}</span>
             </button>
           `).join('')}
        </div>
        <button class="close-modal w-full mt-4 py-2 border border-accent-taupe rounded-lg text-text-heading hover:bg-accent-sand transition-colors">Cancel</button>
      </div>
    `;

    document.body.appendChild(modal);

    // Add timeout cleanup for modal
    let modalCleanupTimer: NodeJS.Timeout;

    // Add event listeners
    modal.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('close-modal') || target === modal) {
        if (document.body.contains(modal)) {
          document.body.removeChild(modal);
        }
        if (modalCleanupTimer) clearTimeout(modalCleanupTimer);
      } else if (target.closest('.share-option')) {
        const option = target.closest('.share-option') as HTMLElement;
        const action = option.dataset.action;
        const url = option.dataset.url;

        if (action === 'copy') {
          navigator.clipboard.writeText(shareUrls[0]).then(() => {
            alert('Link copied to clipboard!');
          }).catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = shareUrls[0];
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Link copied to clipboard!');
          });
        } else if (url) {
          window.open(url, '_blank');
        }
        if (document.body.contains(modal)) {
          document.body.removeChild(modal);
        }
        if (modalCleanupTimer) clearTimeout(modalCleanupTimer);
      }
    });

    // Auto cleanup after 30 seconds
    modalCleanupTimer = setTimeout(() => {
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
    }, 30000);
  };

  return (
    <div className="p-3 md:p-6">
      {/* Mobile-optimized header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-4">My Gallery</h1>

        {/* Mobile-first responsive controls */}
        <div className="flex flex-col space-y-3 md:flex-row md:space-y-0 md:space-x-4 md:items-center md:justify-between">
          {/* Search input - full width on mobile, constrained on desktop so gap shows before dropdown */}
          <div className="relative flex-1 md:flex-none md:w-[20rem] min-w-0">
            <input
              type="text"
              placeholder="Search prompts... (min 2 characters)"
              className="w-full pl-10 pr-4 py-3 md:py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-500 text-base md:text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="absolute left-3 top-3.5 md:top-2.5 h-5 w-5 text-text-description"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {/* Show hint when user has typed 1 character */}
            {searchTerm.length === 1 && (
              <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-accent-sand text-text-description text-xs rounded border">
                Type at least 2 characters to search
              </div>
            )}
          </div>

          {/* Filter dropdown - full width on mobile, fixed width on desktop, at end with space from search */}
          <div className="w-full md:w-52 shrink-0">
            <CustomSelect
              value={filter}
              onChange={(value) => {
                const newFilter = value as typeof filter;
                setFilter(newFilter);
                setCurrentPage(1); // Reset to first page when filter changes
                setGenerations([]); // Add this line
                updateURLParams({ type: newFilter, page: 1 });
              }}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'photoshoot', label: 'Product Photoshoots' },
                { value: 'try-on', label: 'Clothes AI Photoshoot' },
                { value: 'realistic-try-on', label: 'Advance Clothes Photoshoot' },
                { value: 'ai-poses', label: 'Pose Change' },
                { value: 'fashion-extractor', label: 'Outfit Extractor (Flatlay)' },
                { value: 'ghost-mannequin', label: 'Ghost Mannequin' },
                { value: 'wrinkle-removal', label: 'Wrinkle Removal' },
                { value: 'image-editing', label: 'Image Editing' },
                { value: 'video-generation', label: 'Video Generation' },
                { value: 'upscaler', label: 'Image Upscaler' },
                { value: 'background-removal', label: 'Background Removal' },
                { value: 'background-change', label: 'Background Change' },
                { value: 'ai-designer', label: 'AI Designer' },
                { value: 'ai-workflow', label: 'AI Workflow' },
                { value: 'vton', label: 'VTON' },
              ]}
            />
          </div>
        </div>
      </div>

      {uiState.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {Array.from({ length: itemsPerPage }, (_, index) => (
            <div
              key={`skeleton-${index}`}
              className="bg-white rounded-lg shadow-sm flex flex-col overflow-hidden border border-accent-taupe"
            >
              {/* Image skeleton */}
              <div className="relative">
                <div className="aspect-square md:aspect-4/3 bg-accent-taupe animate-pulse">
                  <div className="w-full h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
                </div>
                {/* Badge skeleton */}
                <div className="absolute top-2 right-2">
                  <div className="w-16 h-6 bg-gray-300 animate-pulse rounded-full"></div>
                </div>
              </div>

              {/* Content skeleton */}
              <div className="p-3 md:p-4 flex flex-col flex-grow">
                {/* Header info skeleton */}
                <div className="flex items-center justify-between mb-2">
                  <div className="h-4 bg-gray-300 animate-pulse rounded w-20"></div>
                  <div className="h-4 bg-gray-300 animate-pulse rounded w-16"></div>
                </div>

                {/* Prompt text skeleton */}
                <div className="mb-3 space-y-2">
                  <div className="h-4 bg-gray-300 animate-pulse rounded w-full"></div>
                  <div className="h-4 bg-gray-300 animate-pulse rounded w-3/4"></div>
                </div>

                {/* Action buttons skeleton */}
                <div className="mt-auto">
                  <div className="flex space-x-2">
                    <div className="flex-1 h-10 md:h-8 bg-gray-300 animate-pulse rounded-md"></div>
                    <div className="flex-1 h-10 md:h-8 bg-gray-300 animate-pulse rounded-md"></div>
                    <div className="flex-1 h-10 md:h-8 bg-gray-300 animate-pulse rounded-md"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">
          <p className="text-sm md:text-base">{error}</p>
        </div>
      ) : (
        <>
          {uiState.showGrid && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {filteredGenerations.map((generation, index) => (
                <div
                  key={generation.generationId}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col overflow-hidden border border-accent-taupe"
                >
                  {/* Image section with mobile-optimized aspect ratio */}
                  <div className="relative">
                    <div className="aspect-square md:aspect-4/3">
                      {generation.imageUrlList && generation.imageUrlList.length > 0 ? (
                        <ProgressiveImage
                          src={generation.imageUrlList[0]}
                          alt={generation.prompt || 'Generated image'}
                          className="w-full h-full"
                          priority={index < 6}
                        />
                      ) : (
                        <div className="w-full h-full bg-accent-sand flex items-center justify-center">
                          <div className="text-center text-text-description">
                            <i className="fas fa-exclamation-triangle text-2xl mb-2"></i>
                            <p className="text-xs">Generation failed</p>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Badge - larger for mobile */}
                    {(generation.numberOfImages > 0 || generation.numberOfVideos > 0) && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 shadow-sm">
                          {generation.numberOfImages > 0 && (
                            <span>
                              {generation.numberOfImages} Image{generation.numberOfImages !== 1 ? 's' : ''}
                            </span>
                          )}
                          {generation.numberOfImages > 0 && generation.numberOfVideos > 0 && (
                            <span className="mx-1">+</span>
                          )}
                          {generation.numberOfVideos > 0 && (
                            <span>
                              {generation.numberOfVideos} Video{generation.numberOfVideos !== 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content section with mobile-optimized spacing */}
                  <div className="p-3 md:p-4 flex flex-col flex-grow">
                    {/* Header info - mobile responsive */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs md:text-sm font-medium text-text-description truncate">
                        {getDisplayType(generation.type, generation.styleName)}
                      </span>
                      <span className="text-xs md:text-sm text-text-description ml-2 flex-shrink-0">
                        {formatDate(generation.timestamp)}
                      </span>
                    </div>

                    {/* Prompt text with mobile optimization */}
                    {generation.prompt && (
                      <p className="text-sm md:text-sm text-text-heading line-clamp-2 mb-3 leading-relaxed">
                        {generation.prompt}
                      </p>
                    )}

                    {/* Final final FINAL smart responsive action buttons */}
                    <div className="mt-auto">
                      <div className="flex space-x-1 sm:space-x-1.5">
                        <button
                          className={`flex-1 py-1.5 md:py-2 px-2 rounded text-xs sm:text-sm font-medium transition-colors duration-200 touch-manipulation flex items-center justify-center whitespace-nowrap ${downloadingIds.has(generation.generationId)
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-secondary-500 text-white hover:bg-secondary-600'
                            }`}
                          onClick={() => handleDownloadAll(generation)}
                          disabled={downloadingIds.has(generation.generationId)}
                        >
                          {downloadingIds.has(generation.generationId) ? (
                            <>
                              <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1 sm:mr-0 2xl:mr-1"></span>
                              <span className="inline sm:hidden 2xl:inline">Downloading...</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-download text-xs sm:text-sm mr-1 sm:mr-0 2xl:mr-1"></i>
                              <span className="inline sm:hidden 2xl:inline">Download</span>
                            </>
                          )}
                        </button>
                        <button
                          className="flex-1 bg-green-600 text-white py-1.5 md:py-2 px-2 rounded text-xs sm:text-sm font-medium transition-colors duration-200 touch-manipulation flex items-center justify-center hover:bg-green-700 whitespace-nowrap"
                          onClick={() => handleShare(generation)}
                        >
                          <i className="fas fa-share-alt text-xs sm:text-sm mr-1 sm:mr-0 2xl:mr-1"></i>
                          <span className="inline sm:hidden 2xl:inline">Share</span>
                        </button>
                        <button
                          className="flex-1 bg-accent-sand text-text-heading py-1.5 md:py-2 px-2 rounded hover:bg-accent-taupe text-xs sm:text-sm font-medium transition-colors duration-200 touch-manipulation flex items-center justify-center whitespace-nowrap"
                          onClick={() => handleRecreate(generation)}
                        >
                          <i className="fas fa-eye text-xs sm:text-sm mr-1 sm:mr-0 2xl:mr-1"></i>
                          <span className="inline sm:hidden 2xl:inline">View</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {uiState.showEmpty && (
            <div className="text-center py-12 px-4">
              <svg
                className="mx-auto h-16 md:h-12 w-16 md:w-12 text-text-description mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-lg md:text-sm font-medium text-text-heading mb-2">
                No generations found
              </h3>
              <p className="text-base md:text-sm text-text-description leading-relaxed max-w-md mx-auto">
                {searchTerm || filter !== 'all'
                  ? "Try adjusting your search or filter to find what you're looking for."
                  : "Start creating photoshoots or try-ons to see them here."}
              </p>

              {/* Mobile-friendly action buttons in empty state */}
              {!searchTerm && filter === 'all' && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                  <button
                    onClick={() => navigate('/photoshoot')}
                    className="bg-secondary-500 text-white py-3 px-6 rounded-lg hover:bg-secondary-600 font-medium transition-colors duration-200 touch-manipulation"
                  >
                    <i className="fas fa-camera mr-2"></i>
                    Start Photoshoot
                  </button>
                  <button
                    onClick={() => navigate('/try-on')}
                    className="bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 font-medium transition-colors duration-200 touch-manipulation"
                  >
                    <i className="fas fa-tshirt mr-2"></i>
                    Try Virtual Try-On
                  </button>
                  <button
                    onClick={() => navigate('/fashion-extractor')}
                    className="bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 font-medium transition-colors duration-200 touch-manipulation"
                  >
                    <i className="fas fa-cut mr-2"></i>
                    Outfit Extractor (Flatlay)
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {uiState.showPagination && paginationInfo && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-4">
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-description">Show:</span>
            <CustomSelect
              value={itemsPerPage.toString()}
              onChange={(value) => {
                const newLimit = Number(value);
                setItemsPerPage(newLimit);
                setCurrentPage(1); // Reset to first page when changing items per page
                updateURLParams({ limit: newLimit, page: 1 });
              }}
              options={[6, 12, 24, 36].map((size) => ({
                value: size.toString(),
                label: size.toString()
              }))}
              className="min-w-[50px] w-[50px]"
              openUpward={true}
              showChevron={false}
            />
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-1 rounded-md text-sm ${paginationInfo.hasPreviousPage
                ? 'bg-secondary-500 text-white hover:bg-secondary-600'
                : 'bg-accent-sand text-text-description cursor-not-allowed'
                }`}
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                updateURLParams({ page: newPage });
              }}
              disabled={!paginationInfo.hasPreviousPage}
            >
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show first page, last page, current page, and pages around current page
                  const showAroundCurrent = Math.abs(page - currentPage) <= 1;
                  const isFirstPage = page === 1;
                  const isLastPage = page === paginationInfo.totalPages;
                  return showAroundCurrent || isFirstPage || isLastPage;
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-text-description">...</span>
                    )}
                    <button
                      className={`w-8 h-8 rounded-md text-sm ${currentPage === page
                        ? 'bg-secondary-500 text-white'
                        : 'bg-accent-sand text-text-description hover:bg-accent-taupe'
                        }`}
                      onClick={() => {
                        setCurrentPage(page);
                        updateURLParams({ page });
                      }}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>

            <button
              className={`px-3 py-1 rounded-md text-sm ${paginationInfo.hasNextPage
                ? 'bg-secondary-500 text-white hover:bg-secondary-600'
                : 'bg-accent-sand text-text-description cursor-not-allowed'
                }`}
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                updateURLParams({ page: newPage });
              }}
              disabled={!paginationInfo.hasNextPage}
            >
              Next
            </button>
          </div>

          {/* Total items counter */}
          <div className="text-sm text-text-description">
            {`${paginationInfo.totalItems} total items`}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery; 